import fileToUrl from '../../../misc/file-to-url'

// 圧縮せずにアップロードできる画像の最大サイズは2MB
const DEFAULT_MAX_IMAGE_SIZE = 1024 * 1024 * 2
const DEFAULT_MAX_IMAGE_WIDTH = 1280
const DEFAULT_MAX_IMAGE_HEIGHT = DEFAULT_MAX_IMAGE_WIDTH
const DEFAULT_QUALITY = 0.85

export default class ImageCompressor {
  maxSize: number = DEFAULT_MAX_IMAGE_SIZE
  maxWidth: number = DEFAULT_MAX_IMAGE_WIDTH
  maxHeight: number = DEFAULT_MAX_IMAGE_HEIGHT
  quality: number = DEFAULT_QUALITY

  /**
   * @param {HTMLCanvasElement} canvas - 画像の圧縮に使用する canvas 要素
   */
  constructor(private readonly canvas: HTMLCanvasElement) {
  }

  /**
   * 画像を圧縮する
   * @param {File} file - 画像ファイル
   * @return {Promise<string>} - DATA URL 形式の画像データ
   */
  async compress(file: File): Promise<string> {
    try {
      return await this.doCompress(file)
    } catch (error) {
      throw `画像の読み込みに失敗しました: ${file.name}`
    }
  }

  async doCompress(file: File): Promise<string> {
    if (file.size <= this.maxSize) {
      return fileToUrl(file)
    }
    const imageElement = await this.loadAsImageElement(file)

    const width = imageElement.width
    const height = imageElement.height
    const ratioImage = width / height
    const ratioMax = this.maxWidth / this.maxHeight
    const sizeRate = (ratioImage < ratioMax)
      ? Math.min(height, this.maxHeight) / height
      : Math.min(width, this.maxWidth) / width

    const canvasWidth = sizeRate * width
    const canvasHeight = sizeRate * height

    const context = this.canvas.getContext('2d')
    this.canvas.width = canvasWidth
    this.canvas.height = canvasHeight
    context.drawImage(imageElement, 0, 0, canvasWidth, canvasHeight)

    return this.canvas.toDataURL(file.type, this.quality)
  }

  private async loadAsImageElement(file: File): Promise<HTMLImageElement> {
    const dataUrl = await fileToUrl(file)
    return new Promise<HTMLImageElement>((resolve, reject) => {
      const image = document.createElement('img')
      image.onload = (ev) => {
        resolve(image)
      }
      image.onerror = (event) => {
        reject(event.error)
      }
      image.src = dataUrl
    })
  }
}
